import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';

const fmtDate = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/* ── shared styles ───────────────────────────────────────────── */
const th = { padding: '0.5rem 0.75rem', background: 'linear-gradient(135deg,#D84315,#F57C00)', color: '#fff', fontWeight: 600, fontSize: '0.8rem', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { padding: '0.45rem 0.75rem', fontSize: '0.875rem', borderBottom: '1px solid #e0e0e0', color: '#2c3e50' };
const inp = { padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: 5, fontSize: '0.875rem', boxSizing: 'border-box' };
const btnStyle = (color = 'primary', small = false) => ({
  padding: small ? '0.3rem 0.65rem' : '0.4rem 0.9rem',
  border: 'none', borderRadius: 5, fontWeight: 600,
  fontSize: small ? '0.75rem' : '0.8rem', cursor: 'pointer',
  background: color === 'primary' ? 'linear-gradient(135deg,#D84315,#F57C00)'
            : color === 'danger'  ? '#e74c3c'
            : '#e0e0e0',
  color: color === 'secondary' ? '#333' : '#fff',
});
const label = { fontSize: '0.8rem', fontWeight: 600, marginBottom: 3, color: '#555', display: 'block' };

/* ── smart autocomplete ──────────────────────────────────────── */
function SmartInput({ placeholder, value, onChange, onSelect, options, width = 240 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = options.filter(o => !value.trim() || o.name.toLowerCase().includes(value.toLowerCase()));

  return (
    <div ref={ref} style={{ position: 'relative', width }}>
      <input style={{ ...inp, width: '100%' }} placeholder={placeholder} value={value} autoComplete="off"
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} />
      {open && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff', border: '1px solid #ccc', borderRadius: 5, maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
          {filtered.map(o => (
            <div key={o.id}
              style={{ padding: '0.4rem 0.7rem', fontSize: '0.85rem', cursor: 'pointer', color: '#2c3e50' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef3ee'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
              onMouseDown={() => { onSelect(o); setOpen(false); }}>
              {o.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── time options 00:00–23:00 ────────────────────────────────── */
const timeOptions = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

const todayDate = () => new Date().toISOString().slice(0, 10);
const nearestHour = () => `${String(new Date().getHours()).padStart(2, '0')}:00`;

const blankForm = (userId = '') => ({
  id: '', captain_id: userId, production_date: todayDate(), production_time: nearestHour(),
  product_id: '', qty: '', return_qty: '', stock_location_id: '',
  subsidiary: 'LH',
});

export default function Production() {
  const { user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${user?.token}` };

  const [productions, setProductions]     = useState([]);
  const [captains, setCaptains]           = useState([]);
  const [products, setProducts]           = useState([]);
  const [locations, setLocations]         = useState([]);

  const myUserId = String(user?.userId || user?.id || '');

  const [form, setForm]               = useState(blankForm(myUserId));
  const [productInput, setProductInput] = useState('');
  const [editing, setEditing]         = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  const [filters, setFilters]         = useState({ captain_id: '', production_date: '', product_id: '' });
  const [searchProdInput, setSearchProdInput] = useState('');

  /* ── load LOVs ── */
  useEffect(() => {
    axios.get(`${API_URL}/commonLOVs/users-by-permission`, { headers, params: { permission: 'production.manage' } })
      .then(r => {
        const list = r.data || [];
        setCaptains(list);
        const myId = String(user?.userId || user?.id || '');
        if (myId && list.some(c => String(c.id) === myId)) {
          setForm(p => ({ ...p, captain_id: myId }));
        }
      }).catch(() => {});
    axios.get(`${API_URL}/commonLOVs/lhproducts-for-location`, { headers }).then(r => setProducts(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/stock/locations`, { headers, params: { type: 'WAREHOUSE', active: 'true' } }).then(r => setLocations(r.data || [])).catch(() => {});
    fetchProductions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProductions = async (f = filters) => {
    try {
      const r = await axios.get(`${API_URL}/production`, { headers, params: { subsidiary: 'LH', ...f } });
      setProductions(r.data || []);
    } catch { setError('Failed to load productions.'); }
  };

  /* ── form handlers ── */
  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const fetchProductsForLocation = async (locId) => {
    try {
      const params = locId ? { location_id: locId } : {};
      const r = await axios.get(`${API_URL}/commonLOVs/lhproducts-for-location`, { headers, params });
      setProducts(r.data || []);
    } catch (err) {
      setError(`Product load failed: ${err.response?.status} – ${err.response?.data?.error || err.message}`);
    }
  };

  const handleLocationChange = (locId) => {
    setForm(p => ({ ...p, stock_location_id: locId, product_id: '' }));
    setProductInput('');
    fetchProductsForLocation(locId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const payload = { ...form, return_qty: form.return_qty || null, stock_location_id: form.stock_location_id || null };
      if (editing) {
        await axios.put(`${API_URL}/production/${form.id}`, payload, { headers });
        setSuccess('Production updated.');
      } else {
        await axios.post(`${API_URL}/production`, payload, { headers });
        setSuccess('Production added.');
      }
      cancelEdit();
      fetchProductions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save production.');
    }
  };

  const handleEdit = async (prod) => {
    setForm({
      ...prod,
      production_date: fmtDate(prod.production_date),
      stock_location_id: prod.stock_location_id || '',
      return_qty: prod.return_qty ?? '',
    });
    setProductInput(prod.product_name || '');
    setEditing(true);
    setError(''); setSuccess('');
    await fetchProductsForLocation(prod.stock_location_id || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = (userId = String(user?.userId || user?.id || '')) => {
    setForm(blankForm(userId));
    setProductInput('');
    fetchProductsForLocation('');
    setEditing(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this production entry?')) return;
    setError(''); setSuccess('');
    try {
      await axios.delete(`${API_URL}/production/${id}`, { headers });
      setSuccess('Production deleted.');
      fetchProductions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete.');
    }
  };

  /* ── search ── */
  const handleSearch = () => fetchProductions(filters);
  const handleReset = () => {
    const blank = { captain_id: '', production_date: '', product_id: '' };
    setFilters(blank);
    setSearchProdInput('');
    fetchProductions(blank);
  };

  const isOld = (dateStr) => user?.role !== 'SADM' && (Date.now() - new Date(dateStr)) > 2 * 24 * 60 * 60 * 1000;

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1100 }}>
      <h2 style={{ marginBottom: '1.25rem', fontSize: '1.25rem', color: '#2c3e50' }}>
        {editing ? 'Edit Production' : 'Production'}
      </h2>

      {error   && <div style={{ background: '#fee', color: '#c33', border: '1px solid #fcc', borderRadius: 6, padding: '0.6rem 1rem', marginBottom: '1rem' }}>{error}</div>}
      {success && <div style={{ background: '#efe', color: '#2a7a2a', border: '1px solid #cfc', borderRadius: 6, padding: '0.6rem 1rem', marginBottom: '1rem' }}>{success}</div>}

      {/* ── Form ── */}
      <section style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '1.1rem', marginBottom: '1.5rem' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>

            <div>
              <span style={label}>Captain</span>
              <select style={{ ...inp, minWidth: 160 }} name="captain_id" value={form.captain_id} onChange={handleChange} required>
                <option value="">-- Captain --</option>
                {captains.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <span style={label}>Date</span>
              <input style={{ ...inp, width: 150 }} type="date" name="production_date" value={form.production_date} onChange={handleChange} required />
            </div>

            <div>
              <span style={label}>Time</span>
              <select style={{ ...inp, width: 110 }} name="production_time" value={form.production_time} onChange={handleChange} required>
                <option value="">-- Time --</option>
                {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <span style={label}>Product</span>
              <SmartInput
                placeholder="Search product..."
                value={productInput}
                options={products}
                width={260}
                onChange={v => { setProductInput(v); if (!v) setForm(p => ({ ...p, product_id: '' })); }}
                onSelect={o => { setProductInput(o.name); setForm(p => ({ ...p, product_id: o.id })); }}
              />
            </div>

            <div>
              <span style={label}>Qty</span>
              <input style={{ ...inp, width: 90 }} type="number" step="any" name="qty" value={form.qty} onChange={handleChange} placeholder="Qty" required />
            </div>

            <div>
              <span style={label}>Return Qty</span>
              <input style={{ ...inp, width: 90 }} type="number" step="any" name="return_qty" value={form.return_qty} onChange={handleChange} placeholder="Optional" />
            </div>

            <div>
              <span style={label}>Stock Location</span>
              <select style={{ ...inp, minWidth: 160 }} name="stock_location_id" value={form.stock_location_id}
                onChange={e => handleLocationChange(e.target.value)}>
                <option value="">-- None --</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
              <button style={btnStyle()} type="submit">{editing ? 'Update' : 'Add'}</button>
              {editing && <button style={btnStyle('secondary')} type="button" onClick={cancelEdit}>Cancel</button>}
            </div>
          </div>
        </form>
      </section>

      {/* ── Search filters ── */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
        <div>
          <span style={label}>Captain</span>
          <select style={{ ...inp, minWidth: 150 }} value={filters.captain_id} onChange={e => setFilters(p => ({ ...p, captain_id: e.target.value }))}>
            <option value="">All Captains</option>
            {captains.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <span style={label}>Date</span>
          <input style={{ ...inp, width: 150 }} type="date" value={filters.production_date}
            onChange={e => setFilters(p => ({ ...p, production_date: e.target.value }))} />
        </div>
        <div>
          <span style={label}>Product</span>
          <SmartInput
            placeholder="Search product..."
            value={searchProdInput}
            options={products}
            width={220}
            onChange={v => { setSearchProdInput(v); if (!v) setFilters(p => ({ ...p, product_id: '' })); }}
            onSelect={o => { setSearchProdInput(o.name); setFilters(p => ({ ...p, product_id: o.id })); }}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
          <button style={btnStyle()} onClick={handleSearch}>Search</button>
          <button style={btnStyle('secondary')} onClick={handleReset}>Reset</button>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Captain</th>
              <th style={th}>Date</th>
              <th style={th}>Time</th>
              <th style={th}>Product</th>
              <th style={th}>Qty</th>
              <th style={th}>Return Qty</th>
              <th style={th}>Stock Location</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {productions.length === 0 ? (
              <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#999', padding: '1.5rem' }}>No records found.</td></tr>
            ) : productions.map(p => {
              const disabled = isOld(p.production_date);
              return (
                <tr key={p.id}>
                  <td style={td}>{p.captain_name}</td>
                  <td style={td}>{fmtDate(p.production_date)}</td>
                  <td style={td}>{p.production_time}</td>
                  <td style={td}>{p.product_name}</td>
                  <td style={td}>{p.qty}</td>
                  <td style={td}>{p.return_qty ?? '—'}</td>
                  <td style={td}>{p.stock_location_name || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button style={{ ...btnStyle('secondary', true), marginRight: 4, opacity: disabled ? 0.5 : 1 }}
                      disabled={disabled} onClick={() => handleEdit(p)}>Edit</button>
                    <button style={{ ...btnStyle('danger', true), opacity: disabled ? 0.5 : 1 }}
                      disabled={disabled} onClick={() => handleDelete(p.id)}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
